import React from "react";
import PropTypes from "prop-types";
import { withTheme } from "styled-components";
import formatFieldErrors from "lib/format-field-errors";
import FieldError from "components/FieldError";
import {
  FilledLabelWrapper,
  InputTextArea,
  InputTextAreaWrapper,
  LimitContainer,
  MessageWrapper,
  MaterialLabelWrapper,
  OuterInputWrapper,
  RequiredWrapper,
} from "components/Shared/Input";

function TextAreaComponent({
  disabled,
  styleType,
  label,
  placeholder,
  meta: { active, success, submitError, touched, error: syncError } = {},
  TrailingIcon,
  LeadingIcon,
  message,
  onClick,
  input: { onBlur, onChange, onFocus, name, value, ...restOfInput },
  inputMode,
  id,
  hideLimitCounter,
  showRequiredAsterisk,
  readOnly,
  type,
  inputLimit,
  ...props
}) {
  const errors = formatFieldErrors({
    submitError,
    syncError,
  });
  const hasError = touched && errors && errors.length > 0;
  const isMaterialInput = styleType === "material" || !styleType;
  const enteredTextLength = value ? value.length : 0;

  return (
    <OuterInputWrapper disabled={disabled}>
      {(isMaterialInput && (
        <MaterialLabelWrapper
          isActive={active || value.length}
          hasLeadingIcon={LeadingIcon}
          htmlFor={id}
          hideLabelForPlaceholder={!!placeholder}
          showRequiredAsterisk={showRequiredAsterisk}
        >
          {label}
        </MaterialLabelWrapper>
      )) || (
        <FilledLabelWrapper htmlFor={id} styleType={styleType}>
          {showRequiredAsterisk ? <RequiredWrapper>*</RequiredWrapper> : null}
          {label}
        </FilledLabelWrapper>
      )}
      <InputTextAreaWrapper
        styleType={styleType || "material"}
        success={success}
        error={hasError}
        touched={touched}
        readOnly={readOnly}
      >
        <InputTextArea
          id={id}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={onChange}
          name={name}
          type={type}
          maxLength={inputLimit}
          inputMode={inputMode}
          readOnly={readOnly}
          {...restOfInput}
        />
      </InputTextAreaWrapper>
      {inputLimit && !hideLimitCounter && (
        <LimitContainer>{`${enteredTextLength}/${inputLimit}`}</LimitContainer>
      )}
      {touched && <MessageWrapper success={success} touched={touched} />}
      <FieldError show={hasError} errors={errors} />
    </OuterInputWrapper>
  );
}

export default withTheme(TextAreaComponent);

TextAreaComponent.propTypes = {
  placeholder: PropTypes.string,
  TrailingIcon: PropTypes.any,
  LeadingIcon: PropTypes.func,
  colorType: PropTypes.oneOf([
    "primary",
    "secondary",
    "danger",
    "warning",
    "success",
  ]),
  styleType: PropTypes.oneOf(["material", "filled"]),
  outlineType: PropTypes.oneOf(["solid", "subtle", "outline"]),
  meta: PropTypes.shape({
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object,
      PropTypes.string,
    ]),
    submitError: PropTypes.bool,
    success: PropTypes.bool,
    touched: PropTypes.bool,
  }),
  onClick: PropTypes.func,
  message: PropTypes.node,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  inputLimit: PropTypes.number,
  hideLimitCounter: PropTypes.bool,
  type: PropTypes.string,
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  trailingIconOnClick: PropTypes.func,
  theme: PropTypes.shape({
    colors: PropTypes.shape({
      primary: PropTypes.string,
    }),
  }),
  inputMode: PropTypes.string,
  id: PropTypes.string,
  showRequiredAsterisk: PropTypes.bool,
  readOnly: PropTypes.bool,
};

TextAreaComponent.defaultProps = {
  placeholder: null,
  styleType: "material",
  TrailingIcon: null,
  LeadingIcon: null,
  colorType: "primary",
  meta: { error: false, success: false, submitError: false, touched: false },
  outlineType: "solid",
  onClick: () => {},
  message: "",
  disabled: false,
  label: null,
  inputLimit: null,
  input: {
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    name: undefined,
    value: "",
  },
  hideLimitCounter: false,
  trailingIconOnClick: () => {},
  theme: { primary: null },
  type: "textarea",
  inputMode: "text",
  id: undefined,
  showRequiredAsterisk: false,
  readOnly: false,
};
